/**
 * Mapping a legacy sale Issue onto a Service Center issue.
 *
 * A sale Issue lives at projects/{p}/sales/{s}/issues/{id} and describes a
 * material problem — damage, missing, backorder — in the fulfilment flow. The
 * Service Center tracks the same problem as a ServiceIssue once it needs a
 * visit. Two callers build that ServiceIssue and must agree on every field:
 * the CONVERT_ISSUE_TO_SERVICE_ISSUE store action and
 * scripts/backfill-service-center.mjs (which converts the Issues that were
 * open when the Service Center shipped). Pure module, no app imports, so the
 * script can load it under plain Node.
 */

// A sale Issue's "damage" is any damage — a dented panel, a chipped sash —
// so it maps to "other", not "broken_glass": Broken Glass asks for the glass
// size, break type and grids, which most of them don't have.
export const ISSUE_TYPE_TO_SERVICE_ISSUE_TYPE = {
  damage: "other",
  missing: "hardware",
  backorder: "other",
};

/**
 * The ServiceIssue data for a legacy Issue.
 *
 * @param {object} args
 * @param {string} args.issueID      the Issue's id (kept as originalIssueID)
 * @param {string} args.projectID
 * @param {string} args.saleID
 * @param {object} args.issue        the Issue's stored data
 * @param {string} [args.serviceType] warranty by default
 * @param {Date}   [args.now]        stamp for a resolved issue's completedDate
 * @param {string} [args.contactID]  service contact, when the caller has one
 * @param {object} [args.serviceAddress] { street, city, state, zip }, when
 *   the caller has one; omitted otherwise so the class default applies
 */
export function serviceIssueDataFromIssue({
  issueID,
  projectID,
  saleID,
  issue,
  serviceType = "warranty",
  now = new Date(),
  contactID = "",
  serviceAddress = null,
}) {
  const data = {
    // Link to original project/sale/item
    projectID,
    saleID,
    itemID: issue.itemID || "",
    laborID: issue.laborID || "",
    quantity: issue.quantity || 1,

    // Service-specific fields
    serviceType,
    issueType: ISSUE_TYPE_TO_SERVICE_ISSUE_TYPE[issue.type] || "other",
    issueTypeDetails: {},

    // Description & resolution
    description: issue.description || "",
    resolution: issue.resolution || "",
    isResolved: issue.isResolved || false,

    // Default urgency and scheduling. The scale is 1 = punch list,
    // 2 = typical, 3 = urgent, and every urgent gate tests `urgency === 3`,
    // so a critical issue must map to 3 — mapping it to 2 hid it from the hot
    // list, metrics and badges.
    urgency: issue.isCritical ? 3 : 1,
    estimatedHours: 1,
    techsRequired: 1,

    // Parts tracking (from original if order was required)
    partsRequired: issue.orderRequired || false,
    partsOrdered: Boolean(issue.orders && issue.orders.length > 0),
    partsReceived: false,
    partsNotes: "",

    // Dates
    reportedDate: issue.created || now,
    completedDate: issue.isResolved ? now : null,

    // Media. Both Issue and ServiceIssue store the same DocumentRef shape
    // ({ type, info, file: { name, url } }) - just retag the type so it
    // matches what a ServiceIssue's own media picker writes.
    media: (issue.media || []).map((doc) => ({
      ...doc,
      type: "service-issue-media",
    })),

    // Billing
    billableTo: issue.billableTo || "vendor",

    // Flags
    isCritical: issue.isCritical || false,

    // Reference to original issue
    originalIssueID: issueID,

    // Whoever reported the Issue reported this problem. The store action's
    // SAVE_SERVICE_ISSUE still stamps the signed-in user, because the rules
    // only let a client create an issue as itself.
    createdBy: issue.createdBy || null,

    contactID,
  };
  if (serviceAddress) {
    data.serviceAddress = serviceAddress;
  }
  return data;
}

/**
 * The contact a new service issue is prefilled with, in the order the New
 * Service Issue dialog uses: the sale's delivery contact (the person on site
 * for the delivery), then the sale's sold-to contact, then the project's.
 * Takes STORED data (`sale.deliveryContact`, not a class instance).
 */
export function pickServiceContactID({ sale, project } = {}) {
  return (
    (sale && sale.deliveryContact) ||
    (sale &&
      sale.soldTo &&
      sale.soldTo.address &&
      sale.soldTo.address.contact) ||
    (project && project.soldTo && project.soldTo.contact) ||
    ""
  );
}

/**
 * The service address for a project's issue — the project address, falling
 * back to the sale's ship-to (the same order buildPunchoutWorkOrder uses).
 * Returns null when neither carries a street or city, so the caller can leave
 * the field to its default rather than store an empty address.
 */
export function serviceAddressFor({ project, sale } = {}) {
  const source = (project && project.address) || (sale && sale.shipTo) || null;
  if (!source || !(source.street || source.city)) return null;
  return {
    street: source.street || "",
    city: source.city || "",
    state: source.state || "",
    zip: source.zip || "",
  };
}
